/**
 * Ranked-item tracker.
 *
 * Unique weapons and armour are priced on bonuses and rarity rather than a
 * market average, so this panel filters on those instead of a profit margin.
 * Candidates are merged from weav3r (which indexes bazaars) and Torn's own
 * item-market endpoint.
 */

import { catalog$, isRankedType } from "../data/catalog.ts";
import { rankedRows$ } from "../engine/scheduler.ts";
import { rankedWatchId } from "../engine/ranked.ts";
import { ranked$ } from "../state/store.ts";
import type { Offer, RankedWatch } from "../state/types.ts";
import { buyUrl, itemImage, WEAPON_BONUS_NAMES } from "./links.ts";
import { append, card, clear, el, on } from "./dom.ts";
import { createTypeahead } from "./typeahead.ts";
import { digitsOnly, groupDigits, money, moneyExact, relativeTime } from "../util/format.ts";

function patchWatch(id: string, patch: Partial<RankedWatch>): void {
  ranked$.update((watches) =>
    watches.map((watch) => (watch.id === id ? { ...watch, ...patch } : watch)),
  );
}

export function createAddRankedPanel(): HTMLElement {
  const feedback = el("p", { class: "tiny mb-0 mt-2 d-none" });

  const typeahead = createTypeahead({
    placeholder: "Search weapons, armor, clothing…",
    filter: isRankedType,
    onPick: (item) => {
      ranked$.update((watches) => [
        ...watches,
        {
          id: rankedWatchId(),
          itemId: item.id,
          itemName: item.name,
          itemType: item.type,
          mode: "bargain",
          maxPrice: null,
          rarityFloor: null,
          requiredBonuses: [],
          minBonusValue: 0,
          addedAt: Date.now(),
        },
      ]);
      feedback.textContent = `Tracking ${item.name}. Set a price ceiling and filters on the row.`;
      feedback.className = "tiny mb-0 mt-2 text-success-emphasis";
    },
    disabledReason: () =>
      catalog$.get().items.length === 0 ? "Add a valid API key to load the item catalog…" : null,
  });

  catalog$.subscribe(() => typeahead.refresh(), false);

  const note = el(
    "div",
    { class: "alert alert-warning tiny py-2 px-3 mb-3" },
    el("strong", { text: "Note: " }),
    "searches the Item Market (live, via Torn) and player bazaars (via weav3r). Display-case " +
      "entries are filtered out — they show a price but cannot be bought.",
  );

  return card("Add ranked item", el("div", {}, note, typeahead.root, feedback));
}

function bonusPicker(watch: RankedWatch): HTMLElement {
  const select = el("select", {
    class: "form-select form-select-sm",
    "aria-label": "Required bonus",
  }) as HTMLSelectElement;

  append(select, el("option", { value: "", text: "Any bonus" }));
  for (const name of WEAPON_BONUS_NAMES) {
    append(select, el("option", { value: name, text: name }));
  }

  select.value = watch.requiredBonuses[0] ?? "";
  on(select, "change", () => {
    patchWatch(watch.id, { requiredBonuses: select.value ? [select.value] : [] });
  });

  return select;
}

function rarityPicker(watch: RankedWatch): HTMLElement {
  const select = el("select", {
    class: "form-select form-select-sm",
    "aria-label": "Minimum rarity",
  }) as HTMLSelectElement;

  for (const [value, label] of [
    ["", "Any rarity"],
    ["yellow", "Yellow+"],
    ["orange", "Orange+"],
    ["red", "Red only"],
  ] as const) {
    append(select, el("option", { value, text: label }));
  }

  select.value = watch.rarityFloor ?? "";
  on(select, "change", () => {
    patchWatch(watch.id, {
      rarityFloor: (select.value || null) as RankedWatch["rarityFloor"],
    });
  });

  return select;
}

function numberBox(
  label: string,
  value: number | null,
  title: string,
  onCommit: (value: number | null) => void,
): HTMLElement {
  const input = el("input", {
    type: "text",
    inputmode: "numeric",
    class: "form-control form-control-sm mono text-end",
    placeholder: "—",
    title,
    value: value === null || value === 0 ? "" : groupDigits(value),
  }) as HTMLInputElement;

  on(input, "input", () => {
    input.value = groupDigits(input.value);
  });

  const commit = () => {
    const digits = digitsOnly(input.value);
    onCommit(digits === "" ? null : Number(digits));
  };
  on(input, "change", commit);
  on(input, "blur", commit);

  return el(
    "div",
    { class: "d-flex flex-column" },
    el("span", { class: "control-label", text: label }),
    input,
  );
}

function renderCandidate(offer: Offer): HTMLElement {
  const details = offer.details;
  const rarity = details?.rarity;

  return el(
    "a",
    {
      class: "list-group-item list-group-item-action d-flex align-items-center gap-3 py-2",
      href: buyUrl(offer),
      target: "_blank",
      rel: "noopener noreferrer",
    },
    el("span", {
      class: `price-figure ${rarity ? `rarity-${rarity}` : ""}`,
      text: money(offer.price),
      title: moneyExact(offer.price),
    }),
    el(
      "span",
      { class: "flex-grow-1 min-w-0 d-flex flex-wrap gap-1 align-items-center" },
      el("span", {
        class: `badge ${offer.source === "bazaar" ? "text-bg-info" : "text-bg-secondary"} tiny-caps`,
        text: offer.source === "bazaar" ? "bazaar" : "market",
      }),
      ...(details?.bonuses ?? []).map((bonus) =>
        el("span", {
          class: "bonus-chip",
          text: `${bonus.name} ${bonus.value}`,
          title: bonus.description ?? "",
        }),
      ),
      details?.damage !== undefined
        ? el("span", { class: "tiny text-body-secondary", text: `dmg ${details.damage.toFixed(1)}` })
        : null,
      details?.accuracy !== undefined
        ? el("span", { class: "tiny text-body-secondary", text: `acc ${details.accuracy.toFixed(1)}` })
        : null,
      offer.sellerName ? el("span", { class: "tiny text-body-secondary", text: offer.sellerName }) : null,
    ),
    el("span", { class: "tiny text-body-secondary text-nowrap", text: "open ↗" }),
  );
}

function renderWatch(watch: RankedWatch): HTMLElement {
  const state = rankedRows$.get().get(watch.id);
  const candidates = state?.candidates ?? [];

  const modeToggle = el("select", {
    class: "form-select form-select-sm",
    "aria-label": "Match mode",
  }) as HTMLSelectElement;
  append(
    modeToggle,
    el("option", { value: "bargain", text: "Bargain (anything special)" }),
    el("option", { value: "filter", text: "Filter (my criteria)" }),
  );
  modeToggle.value = watch.mode;
  on(modeToggle, "change", () => {
    patchWatch(watch.id, { mode: modeToggle.value as RankedWatch["mode"] });
  });

  const removeBtn = el("button", {
    type: "button",
    class: "btn btn-sm btn-outline-danger",
    text: "×",
    "aria-label": `Stop tracking ${watch.itemName}`,
  });
  on(removeBtn, "click", () => {
    ranked$.update((watches) => watches.filter((entry) => entry.id !== watch.id));
  });

  const filters = el(
    "div",
    { class: "row g-2 mt-1" },
    el("div", { class: "col-6 col-lg-3" }, modeToggle),
    el(
      "div",
      { class: "col-6 col-lg-3" },
      numberBox("Max price", watch.maxPrice, "Ignore anything above this.", (value) =>
        patchWatch(watch.id, { maxPrice: value }),
      ),
    ),
  );

  // The precise filters only matter outside bargain mode.
  if (watch.mode === "filter") {
    append(
      filters,
      el("div", { class: "col-6 col-lg-2" }, rarityPicker(watch)),
      el("div", { class: "col-6 col-lg-2" }, bonusPicker(watch)),
      el(
        "div",
        { class: "col-6 col-lg-2" },
        numberBox("Min bonus", watch.minBonusValue || null, "Minimum bonus value.", (value) =>
          patchWatch(watch.id, { minBonusValue: value ?? 0 }),
        ),
      ),
    );
  }

  const results = el("div", { class: "list-group list-group-flush mt-2" });

  if (state?.error) {
    append(results, el("div", { class: "tiny text-danger py-2", text: state.error }));
  } else if (candidates.length === 0) {
    append(
      results,
      el("div", {
        class: "tiny text-body-secondary py-2",
        text: state?.lastCheckedAt ? "No matches right now." : "Not checked yet — press START.",
      }),
    );
  } else {
    for (const offer of candidates.slice(0, 8)) append(results, renderCandidate(offer));
    if (candidates.length > 8) {
      append(
        results,
        el("div", {
          class: "tiny text-body-secondary py-2",
          text: `+${candidates.length - 8} more matches`,
        }),
      );
    }
  }

  return el(
    "div",
    { class: "p-3 border-bottom" },
    el(
      "div",
      { class: "d-flex align-items-center gap-2" },
      el("img", {
        src: itemImage(watch.itemId),
        alt: "",
        class: "item-thumb",
        loading: "lazy",
        width: 46,
        height: 26,
      }),
      el(
        "div",
        { class: "flex-grow-1 min-w-0" },
        el("div", { class: "fw-semibold text-truncate", text: watch.itemName }),
        el("div", {
          class: "tiny text-body-secondary",
          text: `${candidates.length} match${candidates.length === 1 ? "" : "es"} · checked ${relativeTime(
            state?.lastCheckedAt ?? null,
          )}`,
        }),
      ),
      state?.loading ? el("span", { class: "spinner-border spinner-border-sm text-secondary" }) : null,
      removeBtn,
    ),
    filters,
    results,
  );
}

export function createRankedPanel(): HTMLElement {
  const list = el("div", {});
  const countBadge = el("span", { class: "badge text-bg-light" });

  function render(): void {
    const watches = ranked$.get();
    clear(list);
    countBadge.textContent = `${watches.length} tracked`;

    if (watches.length === 0) {
      append(
        list,
        el(
          "div",
          { class: "text-center text-body-secondary py-4" },
          el("p", { class: "tiny mb-0", text: "No ranked items tracked yet." }),
        ),
      );
      return;
    }

    for (const watch of watches) append(list, renderWatch(watch));
  }

  ranked$.subscribe(render, false);
  rankedRows$.subscribe(render, false);
  render();

  return card("Ranked tracker", el("div", { class: "p-0" }, list), countBadge);
}
